import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, UploadCloud, ImageOff } from "lucide-react";
import { toast } from "sonner";

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export default function StudioBranding() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/tenant/me").then((r) => {
      if (alive) {
        setTenant(r.data);
        setLoading(false);
      }
    }).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  if (loading || !tenant) {
    return (
      <StudioLayout tenant={tenant}>
        <div className="text-white/50 text-sm uppercase tracking-widest" data-testid="studio-loading">Loading studio…</div>
      </StudioLayout>
    );
  }

  return (
    <StudioLayout tenant={tenant}>
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Branding</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">{tenant.name}</h1>
        <p className="text-white/50 mt-1">Slug · <span className="font-mono text-white/70">{tenant.slug}</span></p>
      </div>
      <BrandingForm tenant={tenant} onSaved={(t) => setTenant(t)} />
    </StudioLayout>
  );
}

function BrandingForm({ tenant, onSaved }) {
  const [form, setForm] = useState({
    name: tenant.name,
    tagline: tenant.tagline,
    logo_url: tenant.logo_url || "",
    theme_color: tenant.theme_color,
    accent_color: tenant.accent_color,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!HEX_RE.test(form.theme_color) || !HEX_RE.test(form.accent_color)) {
      setError("Colors must be valid hex (#RRGGBB)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.patch("/tenant/me", form);
      toast.success("Branding saved");
      onSaved(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={onSubmit} className="lg:col-span-2 bg-[#121212] border border-white/10 p-6 space-y-5" data-testid="studio-branding-form">
        <Row label="Name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="studio-branding-name" />
        </Row>
        <Row label="Tagline">
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="studio-branding-tagline" />
        </Row>
        <Row label="Logo">
          <LogoField value={form.logo_url} onChange={(v) => set("logo_url", v)} />
        </Row>
        <div className="grid grid-cols-2 gap-4">
          <Row label="Theme color">
            <ColorInput value={form.theme_color} onChange={(v) => set("theme_color", v)} testid="studio-branding-theme" />
          </Row>
          <Row label="Accent color">
            <ColorInput value={form.accent_color} onChange={(v) => set("accent_color", v)} testid="studio-branding-accent" />
          </Row>
        </div>

        {error && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3">{error}</div>}

        <div className="flex items-center justify-between border-t border-white/10 pt-4">
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/30">Slug · {tenant.slug} (read-only)</span>
          <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="studio-branding-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save branding"}
          </Button>
        </div>

        {!tenant.white_label_enabled && (
          <div className="border border-white/10 bg-black/40 p-4 mt-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">White label</div>
            <p className="mt-1 text-sm text-white/60 font-light">
              "Powered by Vawworld" footer is currently visible on your storefront.
              <span className="text-white/80"> Upgrade to hide.</span>
            </p>
          </div>
        )}
      </form>

      <div className="bg-[#121212] border border-white/10 p-6" data-testid="studio-branding-preview">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">Live preview</div>
        <div className="aspect-[4/5] relative overflow-hidden border border-white/10" style={{ background: `linear-gradient(135deg, ${form.theme_color}26, #0a0a0a 60%)` }}>
          <div className="absolute inset-0 grain" />
          <div className="relative z-10 p-5 flex flex-col h-full">
            <div className="flex items-center gap-2">
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt=""
                  className="h-11 w-auto max-w-[200px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <>
                  <div className="h-8 w-8 grid place-items-center text-[10px] font-bold tracking-widest" style={{ backgroundColor: form.theme_color }}>
                    {(form.name || "X").slice(0, 2).toUpperCase()}
                  </div>
                  <span className="font-display font-bold text-sm">{form.name || "Studio name"}</span>
                </>
              )}
            </div>
            <div className="mt-auto">
              <h3 className="font-display font-bold text-2xl tracking-tighter leading-tight">
                {form.tagline || "Your tagline appears here."}
              </h3>
              <div className="flex items-center gap-2 mt-3">
                <span className="px-2 py-1 text-[10px] uppercase tracking-[0.25em]" style={{ backgroundColor: form.theme_color, color: "#0a0a0a" }}>Subscribe</span>
                <span className="px-2 py-1 text-[10px] uppercase tracking-[0.25em] border" style={{ borderColor: form.accent_color, color: form.accent_color }}>Sign in</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="border border-white/10 p-3">
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40">Theme</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4" style={{ backgroundColor: form.theme_color }} />
              <span className="font-mono text-xs">{form.theme_color}</span>
            </div>
          </div>
          <div className="border border-white/10 p-3">
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40">Accent</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4" style={{ backgroundColor: form.accent_color }} />
              <span className="font-mono text-xs">{form.accent_color}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
    </div>
  );
}

function LogoField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await api.post("/cms/images/direct-upload");
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(data.upload_url, { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      onChange(data.delivery_url);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Logo upload failed — please try again");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-4" data-testid="studio-branding-logo-field">
      {/* Preview box: contains the logo at any proportion without cropping */}
      <div className="h-16 w-28 shrink-0 bg-black border border-white/15 grid place-items-center overflow-hidden">
        {value ? (
          <img src={value} alt="logo preview" className="max-h-full max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <ImageOff className="w-5 h-5 text-white/25" />
        )}
      </div>
      <div className="min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 bg-white text-black text-[10px] font-semibold uppercase tracking-[0.15em] hover:bg-amber-300 transition-colors" data-testid="studio-branding-logo-upload">
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><UploadCloud className="w-3.5 h-3.5" /> {value ? "Replace logo" : "Upload logo"}</>}
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-[10px] uppercase tracking-widest text-white/40 hover:text-red-400" data-testid="studio-branding-logo-remove">
              Remove
            </button>
          )}
        </div>
        <p className="text-[10px] text-white/35">PNG with transparency works best. It scales to fit — proportions are preserved.</p>
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-xs h-8" data-testid="studio-branding-logo" />
      </div>
    </div>
  );
}

function ColorInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={HEX_RE.test(value) ? value : "#000000"}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-10 w-10 bg-transparent border border-white/15 cursor-pointer p-0"
        data-testid={`${testid}-picker`}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono uppercase"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
